import React, { useEffect } from "react";
import {
  Container,
  Row,
  Col,
  ListGroup,
  Image,
  Card,
  Button,
  Alert,
  Spinner,
} from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FaBoxOpen,
  FaTruck,
  FaRupeeSign,
  FaCheckCircle,
  FaArrowLeft,
} from "react-icons/fa";
import Toast from "react-hot-toast";

import { useCreateOrderMutation } from "../slices/orderApiSlice";
import { clearCartItems } from "../slices/cartSlice";

const PlaceOrderScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cart = useSelector((state) => state.cart);

  const {
    cartItems,
    shippingAddress,
    itemsPrice,
    shippingPrice,
    taxPrice,
    totalPrice,
    totalDiscount,
  } = cart;

  const [createOrder, { isLoading, error }] = useCreateOrderMutation();

  /* Redirect if shipping address missing (same as App) */
  useEffect(() => {
    if (!shippingAddress?.address) {
      navigate("/shipping");
    }
  }, [shippingAddress, navigate]);

  /* Place Order */
  const placeOrderHandler = async () => {
    try {
      if (!cartItems || cartItems.length === 0) {
        throw new Error("Cart is empty");
      }

      const products = cartItems.map((item) => ({
        productId: item._id,
        quantity: item.qty,
        price: item.price ?? 0,
        discount: item.discount ?? 0,
        vendorId: item.vendorId?._id || item.vendorId,
      }));

      const res = await createOrder({
        products,
        shippingAddress,
        itemsPrice,
        totalDiscount,
        deliveryCharge: shippingPrice,
        taxPrice,
        totalAmount: totalPrice, // ✅ FINAL PAYABLE AMOUNT
      }).unwrap();

      if (!res.orders || res.orders.length === 0) {
        throw new Error("No orders were created");
      }

      Toast.success("Order placed successfully!");

      dispatch(clearCartItems());

      if (res.orders.length === 1) {
        navigate(`/userOrder/${res.orders[0]._id}`);
      } else {
        navigate("/orders-summary", {
          state: { orders: res.orders },
        });
      }
    } catch (err) {
      Toast.error(
        err?.data?.message || err.message || "Something went wrong"
      );
    }
  };

  return (
    <Container className="py-4">
      {/* 🔙 Back to Home */}
      <Button
        variant="outline-secondary"
        onClick={() => navigate("/")}
        className="mb-4 d-flex align-items-center"
      >
        <FaArrowLeft className="me-2" /> Back to Home
      </Button>

      <Row>
        {/* Left Section */}
        <Col md={8}>
          {/* 🚚 Shipping Info */}
          <Card className="mb-3 shadow-sm rounded border-info">
            <Card.Header className="bg-info text-white">
              <FaTruck className="me-2" />
              <strong>Shipping Information</strong>
            </Card.Header>
            <Card.Body>
              <p>
                <strong>Address:</strong>
                <br />
                {shippingAddress.address},{" "}
                {shippingAddress.area},{" "}
                {shippingAddress.city} - {shippingAddress.pincode}
                <br />
                📞 {shippingAddress.phoneNumber}
              </p>
            </Card.Body>
          </Card>

          {/* 📦 Order Items */}
          <Card className="mb-3 shadow-sm rounded border-success">
            <Card.Header className="bg-success text-white">
              <FaBoxOpen className="me-2" />
              <strong>Order Items</strong>
            </Card.Header>
            <Card.Body>
              {cartItems.length === 0 ? (
                <Alert variant="warning">Your cart is empty.</Alert>
              ) : (
                <ListGroup variant="flush">
                  {cartItems.map((item, index) => {
                    const price = item.price ?? 0;
                    const discount = item.discount ?? 0;
                    const qty = item.qty ?? 0;

                    return (
                      <ListGroup.Item
                        key={index}
                        className="d-flex align-items-center"
                      >
                        <Image
                          src={item.image}
                          alt={item.name}
                          rounded
                          fluid
                          style={{
                            width: "60px",
                            height: "60px",
                            objectFit: "cover",
                          }}
                          className="me-3"
                        />
                        <div className="flex-grow-1">
                          <strong>{item.name}</strong>
                          <br />
                          {qty} × ₹{(price - discount).toFixed(2)} = ₹
                          {(qty * (price - discount)).toFixed(2)}
                        </div>
                      </ListGroup.Item>
                    );
                  })}
                </ListGroup>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* 🧾 Summary */}
        <Col md={4}>
          <Card className="shadow-sm rounded border-primary">
            <Card.Header className="bg-primary text-white">
              <FaRupeeSign className="me-2" />
              <strong>Order Summary</strong>
            </Card.Header>

            <Card.Body>
              <ListGroup variant="flush">
                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Items</span>
                  <strong>₹{itemsPrice.toFixed(2)}</strong>
                </ListGroup.Item>

                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Total Discount</span>
                  <strong>-₹{totalDiscount.toFixed(2)}</strong>
                </ListGroup.Item>

                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Delivery Charge</span>
                  <strong>
                    {shippingPrice === 0
                      ? "FREE"
                      : `₹${shippingPrice.toFixed(2)}`}
                  </strong>
                </ListGroup.Item>

                <ListGroup.Item className="d-flex justify-content-between">
                  <span>Tax</span>
                  <strong>₹{taxPrice.toFixed(2)}</strong>
                </ListGroup.Item>

                <ListGroup.Item className="d-flex justify-content-between fs-5">
                  <strong>Total</strong>
                  <strong>₹{totalPrice.toFixed(2)}</strong>
                </ListGroup.Item>

                {error && (
                  <ListGroup.Item>
                    <Alert variant="danger" className="mb-0">
                      {error?.data?.message ||
                        error?.message ||
                        "Error placing order"}
                    </Alert>
                  </ListGroup.Item>
                )}

                <ListGroup.Item>
                  <Button
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                    onClick={placeOrderHandler}
                    disabled={cartItems.length === 0}
                    variant={
                      cartItems.length === 0 ? "secondary" : "success"
                    }
                    size="lg"
                  >
                    <FaCheckCircle /> Place Order
                  </Button>

                  {isLoading && (
                    <div className="text-center mt-3">
                      <Spinner animation="border" />
                    </div>
                  )}
                </ListGroup.Item>
              </ListGroup>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default PlaceOrderScreen;
